import { PRODUCT_SKUS } from 'lib/constants/product-skus';

// Product SKUs that should show "New" label
export const NEW_PRODUCT_SKUS = [
  PRODUCT_SKUS.ENERGY_FOCUS,
  PRODUCT_SKUS.SLEEP_RESTORE,
  PRODUCT_SKUS.DAILY_MULTIVITAMIN,
] as const;

// Product configuration: order, featured status, images, and display options
export const PRODUCT_CONFIG = {
  // Daily Synbiotic
  [PRODUCT_SKUS.DAILY_SYNBIOTIC]: {
    order: 1,
    isFeatured: true,
    numberOneProbioticImage:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1766174359/npd/numberone.png',
    numberOneProbioticAltText: '#1 Digestive Health Probiotic',
    numberOneProbioticDisclaimer:
      '<sup>‡</sup>Source: SPINS, Combined Amazon + Target Sales data.&nbsp;Last 12 weeks ending in November 2025.',
    numberOneProbioticDisclaimerMobile:
      '<sup>‡</sup>Source: SPINS, Combined Amazon + Target Sales data.<br>Last 12 weeks ending in November 2025.',
    displayName: 'Daily Synbiotic',
    description:
      'Eases bloating, improves regularity, and supports digestive health with 24 targeted probiotic strains + a novel prebiotic.*',
    alphaCode: 'DS-01',
    alphaCodeRegistered: true,
    pillColor: 'var(--products-dm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757357918/npd/npd-ds-7b2ef1785bb33020b81fe0e5d8ef787c9fd3f58c.png',
    showDiscountPill: false,
    pillPosition: 'top-left' as const,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px', // Control size via container height
    },
  },
  // Pediatric Synbiotic
  [PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC]: {
    order: 5,
    isFeatured: false,
    displayName: 'Pediatric Synbiotic',
    description:
      'Supports healthy regularity, skin, and respiratory health for kids and teens (ages 3-17) with 9 probiotic strains + a prebiotic.*',
    mobileDescription: "Supports kids' gut and whole body health.*",
    alphaCode: 'PDS-08',
    alphaCodeRegistered: true,
    pillColor: 'var(--products-dm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757955677/npd/484bcfe52cad8fa63d1c023a5b15bcdd938d195a_1.png',
    showDiscountPill: false,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
  // Vaginal Synbiotic
  [PRODUCT_SKUS.VAGINAL_SYNBIOTIC]: {
    order: 6,
    isFeatured: false,
    displayName: 'Vaginal Synbiotic',
    description: (
      <>
        Rapidly establishes a healthy vaginal microbiome dominated by{' '}
        <i>L. crispatus</i>—the vagina&apos;s most protective bacteria.*
      </>
    ),

    mobileDescription: 'Rapidly establishes a healthy vaginal microbiome.*',
    alphaCode: 'VS-01',
    alphaCodeTrademark: true,
    pillColor: 'var(--products-dm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757955665/npd/3edb1a43e25a44ba430d5e5d3245108e83c61753_1.png',
    showDiscountPill: false,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
  // Energy Restore
  [PRODUCT_SKUS.ENERGY_FOCUS]: {
    order: 3,
    isFeatured: false,
    displayName: 'Focus + Energy',
    description:
      'Delivers steady, caffeine-free energy to enhance focus, sharpen attention, and help fight mental fatigue.*',
    mobileDescription: 'Sharpen attention, fight fatigue.*',
    alphaCode: 'AM-02',
    alphaCodeTrademark: true,
    pillColor: 'var(--products-am-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757360129/npd/npd-am-c2391a6819effb5bc5dd96153dd97f37ef171204.png',
    showDiscountPill: false,
    pillPosition: 'top-left' as const,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
  // Sleep Restore
  [PRODUCT_SKUS.SLEEP_RESTORE]: {
    order: 4,
    isFeatured: false,
    displayName: 'Sleep + Restore',
    description:
      'Provides non-habit forming support for deep, restorative sleep and refreshed mornings.*',
    mobileDescription: 'Fall asleep faster, wake up refreshed.*',
    alphaCode: 'PM-02',
    alphaCodeTrademark: true,
    pillColor: 'var(--products-pm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757360129/npd/5c30859afe8c5e93f33ff5afad0bca175fa37ca5.png',
    showDiscountPill: false,
    pillPosition: 'top-left' as const,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
  // Daily Multivitamin
  [PRODUCT_SKUS.DAILY_MULTIVITAMIN]: {
    order: 1,
    isFeatured: false,
    displayName: 'Daily Multivitamin',
    description:
      'Helps cover daily nutrient gaps for your body and microbiome with 100% daily value of 20 essential vitamins and minerals.*',
    mobileDescription: '100% daily value of 20 essential nutrients.*',
    alphaCode: 'DM-02',
    alphaCodeTrademark: true,
    pillColor: 'var(--products-dm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757360128/npd/1ed897199978b3f156397db8b70efcfd286565d2.png',
    showDiscountPill: false,
    pillPosition: 'top-left' as const,
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
  // Daily Essentials Duo
  'ds-duo': {
    order: 2,
    isFeatured: true,
    displayName: 'Daily Essentials Duo',
    description:
      'Clinically validated probiotic and advanced multivitamin duo for daily health.*',
    mobileDescription:
      'Daily support for gut health and covering nutrient gaps.*',
    alphaCode: undefined, // No alpha code for bundle
    pillColor: 'var(--products-dm-02-dark)',
    image:
      'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757943559/npd/Daily%20Essentials%20Duo.png',
    showDiscountPill: true,
    discountText: 'Bundle + Save 25%',
    imageConfig: {
      objectFit: 'contain' as const,
      containerHeight: '250px',
    },
  },
} as const;

// Daily Essentials Duo product object
export const DAILY_ESSENTIALS_DUO = {
  id: 127,
  name: 'Daily Essentials Duo',
  code: 'ds-duo',
  description: 'DS-01 + DM-02 bundled together',
  price: '89.98',
  state: 'ACTIVE',
  domesticAttributes: {
    defaultShippingMethod: 'free_shipping',
    autoCouponCode: null,
    autoCouponType: null,
    autoCouponAmount: 0,
    unitPrice: 89.98,
    discountUnitPrice: 74.98,
    billingPeriodLength: 30,
    monthlyUnitPrice: 74.98,
    monthlyDiscountUnitPrice: 74.98,
  },
  internationalAttributes: {
    defaultShippingMethod: 'passport_ddp_all',
    autoCouponCode: null,
    autoCouponType: null,
    autoCouponAmount: 0,
    unitPrice: 89.98,
    discountUnitPrice: 74.98,
    billingPeriodLength: 30,
    monthlyUnitPrice: 89.98,
    monthlyDiscountUnitPrice: 74.98,
  },
  image:
    'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1757943559/npd/Daily%20Essentials%20Duo.png',
  productSku: 'ds-duo',
  isBundle: true,
  componentProducts: [
    PRODUCT_SKUS.DAILY_SYNBIOTIC,
    PRODUCT_SKUS.DAILY_MULTIVITAMIN,
  ],
  savings: '5.00',
  order: 2,
  isFeatured: false,
} as const;
